import React, { useState } from "react";
import { Plus, Trash2, Pencil, ArrowUp, ArrowDown, PlayCircle, FileText, Check, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Badge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LessonDialog } from "./LessonDialog";
import { ConfirmDelete } from "./ConfirmDelete";
import { toast } from "sonner";

const swap = (arr, i, j) => { const a = [...arr]; [a[i], a[j]] = [a[j], a[i]]; return a; };
const iconBtn = "h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-muted disabled:opacity-30 transition-colors";

function Mover({ i, len, onMove, testid }) {
  return (
    <>
      <button className={iconBtn} disabled={i === 0} onClick={() => onMove(i, i - 1)} data-testid={`${testid}-up`} title="Move up"><ArrowUp size={15} /></button>
      <button className={iconBtn} disabled={i === len - 1} onClick={() => onMove(i, i + 1)} data-testid={`${testid}-down`} title="Move down"><ArrowDown size={15} /></button>
    </>
  );
}

function TitleEditor({ value, onSave, onCancel }) {
  const [v, setV] = useState(value);
  return (
    <div className="flex items-center gap-2 flex-1">
      <Input autoFocus value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && v.trim() && onSave(v.trim())} data-testid="section-title-input" />
      <button className={iconBtn} onClick={() => v.trim() && onSave(v.trim())} data-testid="section-title-save"><Check size={16} className="text-emerald-600" /></button>
      <button className={iconBtn} onClick={onCancel}><X size={16} /></button>
    </div>
  );
}

function LessonRow({ l, i, len, onMove, onEdit, onDelete }) {
  return (
    <div className="flex items-center gap-3 py-2.5 px-3 rounded-md hover:bg-muted/50" data-testid={`lesson-row-${l.id}`}>
      <PlayCircle size={16} className="text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-navy truncate">{l.title}</div>
        <div className="text-xs text-muted-foreground flex gap-3">
          {l.duration && <span>{l.duration}</span>}
          {l.resource_url && <span className="inline-flex items-center gap-1"><FileText size={11} /> Notes</span>}
          {l.is_preview && <span className="text-crimson font-semibold">Free preview</span>}
          {!l.video_url && <span className="text-amber-600">No video</span>}
        </div>
      </div>
      {!l.video_approved && <Badge tone="amber">Pending approval</Badge>}
      <Mover i={i} len={len} onMove={onMove} testid={`lesson-${l.id}`} />
      <button className={iconBtn} onClick={onEdit} data-testid={`edit-lesson-${l.id}`} title="Edit"><Pencil size={15} /></button>
      <ConfirmDelete testid={`delete-lesson-${l.id}`} title="Delete lesson?" description={`"${l.title}" will be removed permanently.`} onConfirm={onDelete}
        trigger={<button className={`${iconBtn} text-crimson`} data-testid={`delete-lesson-${l.id}`} title="Delete"><Trash2 size={15} /></button>} />
    </div>
  );
}

function SectionCard({ s, i, len, onMoveSection, reload }) {
  const [editing, setEditing] = useState(false);
  const [dialog, setDialog] = useState({ open: false, lesson: null });
  const lessons = s.lessons || [];
  const run = async (fn, msg) => { try { await fn(); if (msg) toast.success(msg); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const moveLesson = (a, b) => run(() => api.post(`/sections/${s.id}/lessons/reorder`, { ids: swap(lessons, a, b).map((x) => x.id) }));

  return (
    <div className="border border-border rounded-lg bg-white" data-testid={`section-${s.id}`}>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-muted/30">
        <span className="text-xs font-bold text-muted-foreground w-16 shrink-0">SECTION {i + 1}</span>
        {editing ? <TitleEditor value={s.title} onCancel={() => setEditing(false)} onSave={(t) => run(() => api.put(`/sections/${s.id}`, { title: t }), "Section renamed").then(() => setEditing(false))} /> : (
          <>
            <h3 className="font-semibold text-navy flex-1 truncate">{s.title}</h3>
            <span className="text-xs text-muted-foreground hidden sm:inline">{lessons.length} lessons</span>
            <Mover i={i} len={len} onMove={onMoveSection} testid={`section-${s.id}`} />
            <button className={iconBtn} onClick={() => setEditing(true)} data-testid={`edit-section-${s.id}`} title="Rename"><Pencil size={15} /></button>
            <ConfirmDelete testid={`delete-section-${s.id}`} title="Delete section?" description={`"${s.title}" and its ${lessons.length} lesson(s) will be removed permanently.`}
              onConfirm={() => run(() => api.delete(`/sections/${s.id}`), "Section deleted")}
              trigger={<button className={`${iconBtn} text-crimson`} data-testid={`delete-section-${s.id}`} title="Delete"><Trash2 size={15} /></button>} />
          </>
        )}
      </div>
      <div className="p-2">
        {lessons.length === 0 && <p className="text-sm text-muted-foreground px-3 py-2">No lessons yet.</p>}
        {lessons.map((l, li) => (
          <LessonRow key={l.id} l={l} i={li} len={lessons.length} onMove={moveLesson}
            onEdit={() => setDialog({ open: true, lesson: l })} onDelete={() => run(() => api.delete(`/lessons/${l.id}`), "Lesson deleted")} />
        ))}
        <Button size="sm" variant="ghost" className="mt-1 text-crimson" onClick={() => setDialog({ open: true, lesson: null })} data-testid={`add-lesson-${s.id}`}><Plus size={14} className="mr-1" /> Add Lesson</Button>
      </div>
      <LessonDialog open={dialog.open} lesson={dialog.lesson} sectionId={s.id} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} onSaved={reload} />
    </div>
  );
}

export function Curriculum({ courseId, sections, reload }) {
  const [title, setTitle] = useState("");
  const addSection = async () => {
    if (!title.trim()) return toast.error("Enter a section title");
    try { await api.post(`/courses/${courseId}/sections`, { title: title.trim() }); setTitle(""); toast.success("Section added"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const moveSection = async (a, b) => {
    try { await api.post(`/courses/${courseId}/sections/reorder`, { ids: swap(sections, a, b).map((x) => x.id) }); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="curriculum">
      {sections.map((s, i) => <SectionCard key={s.id} s={s} i={i} len={sections.length} onMoveSection={moveSection} reload={reload} />)}
      {sections.length === 0 && <p className="text-sm text-muted-foreground">No sections yet. Add your first section below.</p>}
      <div className="flex gap-2 border border-dashed border-border rounded-lg p-3 bg-white">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSection()} placeholder="New section title, e.g. Introduction" data-testid="new-section-title" />
        <Button className="viriks-navy text-white shrink-0" onClick={addSection} data-testid="add-section"><Plus size={14} className="mr-1" /> Add Section</Button>
      </div>
    </div>
  );
}
